import React, { useState, useEffect } from "react";
import {
  Typography,
  Select,
  Button,
  List,
  Spin,
  Row,
  Col,
  message,
  Card,
  Modal,
  Drawer, Divider
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import regionWiseContentManagementApi from "../../services/regionWiseContentManagementApi";
import cityApi from "../../services/cityApi";
import poiApi from "../../services/poiApi";
import "./rearrangLlist.css"

const { Title, Paragraph } = Typography;
const { Option } = Select;

// Sortable POI item
const SortableItem = ({ id, name }: { id: string; name: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: "12px 16px",
    marginBottom: "8px",
    background: "#f9f9f9",
    borderRadius: 8,
    cursor: "grab",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {name}
    </div>
  );
};

const POIRearrangePage: React.FC = () => {
  const [regions, setRegions] = useState<{ id: string; sectionName: string }[]>(
    []
  );
  const [cities, setCities] = useState<CityResponse[]>([]);
  const [pois, setPois] = useState<POIResponse[]>([]);
  const [region, setRegion] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null); // stores cityId
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPois, setLoadingPois] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [cityName, setCityName] = useState<string>("");
  const [cityDescription, setCityDescription] = useState<string>("");
  const [cityFileUrl, setCityFileUrl] = useState<string>("");
  const [originalPois, setOriginalPois] = useState<POIResponse[]>([]);
  const DEFAULT_IMAGE = "/images/city-01.png";

  const isReordered = () => doneClicked && changesMade;

  const [doneClicked, setDoneClicked] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  // Load regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      setLoadingRegions(true);
      try {
        const res =
          await regionWiseContentManagementApi.getRegionWiseContentManagement();
        setRegions(res.data || []);
      } catch {
        messageApi.error("Failed to load regions");
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, []);

  // Load cities when region changes
  useEffect(() => {
    if (!region) {
      setCities([]);
      setOriginalPois([]);
      setCity(null);
      setPois([]);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);
      setCity(null);
      setPois([]);
      try {
        const res = await cityApi.getCitiesByRegionId(region);
        if (res.cityDataResponses && res.cityDataResponses.length > 0) {
          setCities(res.cityDataResponses);
        } else {
          setCities([]); // clear cities list
          messageApi.warning("No cities found for the selected region.");
        }
      } catch {
        messageApi.error("Failed to load cities");
        setCities([]); // reset cities list on error
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [region]);

  // Load POIs when city changes
  useEffect(() => {
    if (!city) {
      setPois([]);
      return;
    }

    const fetchPois = async () => {
      setLoadingPois(true);
      try {
        const res = await poiApi.getPOIByCity(city);
        if (res.length != 0) {
          setPois([]);
          setOriginalPois([]);
          let poiArray: any[] = [];
          if (Array.isArray(res)) {
            poiArray = res;
          } else if (res) {
            poiArray = [res];
          }

          setPois(poiArray);
          setOriginalPois(poiArray); // <-- Save original order
          const selectedCity = cities.find((r) => r.id === city);
          setCityName(selectedCity?.cityName || "");
          setCityDescription(selectedCity.description || "");
          setCityFileUrl(selectedCity.fileUrl || "");
        } else {
          messageApi.warning("No POIs found for the selected city.");
          setPois([]);
          setOriginalPois([]);
        }
      } catch (err) {
        messageApi.error("Failed to load POIs");
      } finally {
        setLoadingPois(false);
      }
    };
    fetchPois();
  }, [city, cities]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = pois.findIndex((p) => p.id === active.id);
    const newIndex = pois.findIndex((p) => p.id === over.id);
    const newOrder = arrayMove(pois, oldIndex, newIndex);
    setPois(newOrder);

    // mark that order is changed
    if (!changesMade) setChangesMade(true); // mark that changes occurred
  };

  const resetReorderFlags = () => {
    setDoneClicked(false);
    setChangesMade(false);
  };

  const handleSave = async () => {
    if (!city) {
      messageApi.error("Please select a city before saving");
      return;
    }

    // Build payload for API
    const payload = {
      cityID: city,
      sortPOIsRequests: pois.map((poi, index) => ({
        poIsID: poi.id,
        sortOrder: index,
      })),
    };

    try {
      await poiApi.saveReorderPOIs(payload);
      messageApi.success("POIs rearranged successfully.");
    } catch (err) {
      messageApi.error("Failed to save POI order");
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const showModal = () => {
    if (!region && !city) {
      messageApi.error("Nothing to discard. Please select a region and city first.");
      return;
    }
    if (pois.length === 0) {
      messageApi.warning("No POIs available to discard for the selected city.");
      return;
    }
    setIsModalOpen(true);
  };

  const handleOk = () => {
    messageApi.info("Changes discarded.");
    setIsModalOpen(false);
    setPois(originalPois);
    resetReorderFlags(); // 🔹 disable Save & Discard again
  };
  const handleCancel = () => setIsModalOpen(false);

  const showsaveModal = () => {
    if (!region) {
      messageApi.error("Please select a region before saving.");
      return;
    }
    if (!city) {
      messageApi.error("Please select a city before saving.");
      return;
    }
    if (pois.length === 0) {
      messageApi.warning("No POIs available to save for the selected city.");
      return;
    }

    setIsSaveModalOpen(true);
  };
  const handleSaveOk = async () => {
    await handleSave();
    setIsSaveModalOpen(false);
    resetReorderFlags(); // 🔹 disable Save & Discard after saving
  };
  const handleSaveCancel = () => setIsSaveModalOpen(false);


  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* Left Section */}
        <Col xs={24} lg={16}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            <Title level={3} style={{ color: "#41787C", marginBottom: 0, flex: 1 }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined style={{ fontSize: 18, color: "#41787C", marginRight: 10 }} />}
                onClick={() => navigate(-1)}
              />
              Rearrange POI
            </Title>
            <div style={{ marginLeft: "auto" }}>
              <Button
                icon={isEditMode ? <CheckOutlined /> : <EditOutlined />}
                onClick={() => {
                  if (isEditMode) {
                    // --- Done Clicked ---
                    setIsEditMode(false);
                    setDoneClicked(true); // allow save/discard
                  } else {
                    // --- Enter Edit Mode ---
                    setIsEditMode(true);
                    setDoneClicked(false); // reset
                    setChangesMade(false); // reset changes
                    setOriginalPois([...pois]); // keep original order for discard
                  }
                }}
                disabled={pois.length === 0}
              >
                {isEditMode ? "Done" : "Edit"}
              </Button>
            </div>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {/* Region Select */}
            <Col xs={24} md={12}>
              <Title level={5}>Select Region<span style={{ color: "red" }}>*</span></Title>
              <Select
                value={region || ""}
                onChange={(val) => setRegion(val || null)}
                style={{ width: "100%" }}
                placeholder="Select a region"
              >
                <Option value="">Select a region</Option>
                {regions.map((r) => (
                  <Option key={r.id} value={r.id}>
                    {r.sectionName}
                  </Option>
                ))}
              </Select>
              {loadingRegions && <Spin size="small" />}
            </Col>

            {/* City Select */}
            <Col xs={24} md={12}>
              <Title level={5}>Select City<span style={{ color: "red" }}>*</span></Title>
              <Select
                value={city || ""}
                onChange={(val) => setCity(val || null)}
                style={{ width: "100%" }}
                placeholder="Select a city"
                disabled={!region}
              >
                <Option value="">Select a city</Option>
                {cities.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.cityName}
                  </Option>
                ))}
              </Select>
              {loadingCities && <Spin size="small" />}
            </Col>
          </Row>

          <div>
            <Title level={5}>List of POIs</Title>
            {loadingPois ? (
              <Spin />
            ) : !city ? (
              <Paragraph>Select a city to view POIs.</Paragraph>
            ) : pois.length === 0 ? (
              <Paragraph>No POIs found for the selected city.</Paragraph>
            ) : isEditMode ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={pois.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {pois.map((poi) => (
                    <SortableItem key={poi.id} id={poi.id} name={poi.business} />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              <List
                dataSource={pois}
                renderItem={(poi) => (
                  <div
                    key={poi.id}
                    style={{
                      padding: "12px 16px",
                      marginBottom: 8,
                      borderRadius: 6,
                      background: "#f9f9f9",
                    }}
                  >
                    {poi.business} {/* Show Business name */}
                  </div>
                )}
              />
            )}
          </div>
        </Col>

        {/* Right Section - Info Panel */}
        <Col xs={24} lg={8}>
          <Card style={{ textAlign: "center", borderRadius: 12 }}>
            <img
              src={cityFileUrl || DEFAULT_IMAGE}
              alt="region"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 8,
                marginBottom: 16,
                maxHeight: 400,
                objectFit: "cover",
              }}
            />
            <Title level={5}>{cityName}</Title>
            <p style={{ color: "#555" }}>{cityDescription}</p>

            {/* Footer Buttons */}
            <div
              style={{
                marginTop: 30,
                display: "flex",
                justifyContent: "center", // centers the whole row
                alignItems: "center",
                gap: 16, // equal space between buttons
                flexWrap: "nowrap",
              }}
              className="responsive-buttons"
            >
              <Button onClick={() => setPreviewVisible(true)} disabled={pois.length === 0}>
                Mobile Preview
              </Button>
              <Button danger onClick={showModal} disabled={!isReordered()}>
                Discard
              </Button>

              <Modal
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={null} // custom footer
                centered
                closable={false}
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Discard Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to discard changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleOk}
                  >
                    Yes
                  </Button>

                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>


              <Button type="primary" onClick={showsaveModal} disabled={!isReordered()}>
                Save
              </Button>

              <Modal
                open={isSaveModalOpen}
                onOk={handleSaveOk}
                onCancel={handleSaveCancel}
                footer={null} // custom footer
                closable={false}   // hides the default X close button
                mask={false}       // disables dark overlay
                maskClosable={false} // clicking outside does NOT close modal
                centered
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Save Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to save the changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveOk} // call your save function here
                  >
                    Yes, Save
                  </Button>

                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>

              <Drawer
                open={previewVisible}
                onClose={() => setPreviewVisible(false)}
                placement="right"
                width={420}
                closable={false}
                bodyStyle={{ padding: 0, display: "flex", justifyContent: "center" }}
              >
                <div
                  style={{
                    width: 360,
                    height: 640,
                    border: "16px solid black",
                    borderRadius: "30px",
                    padding: "0",
                    position: "relative",
                    background: "#f9f9f9",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 12,
                      borderBottom: "1px solid #ddd",
                      fontWeight: "bold",
                      fontSize: 16,
                      color: "#a0522d",
                    }}
                  >
                    <ArrowLeftOutlined style={{ fontSize: 18, cursor: "pointer" }} />
                    <div style={{ flex: 1, textAlign: "center", marginLeft: -18 }}>
                      Places Of Interest's
                    </div>
                    <div style={{ width: 18 }} />
                  </div>

                  {/* Scrollable content */}
                  <div style={{ flex: 1, overflowY: "auto" }}>
                    <div style={{ background: "#fff", padding: 12 }}>
                      <h3 style={{ marginBottom: "-16px" }}>{cityName}</h3>
                      <Divider style={{ borderColor: "#a0522d", borderWidth: 1 }} />
                      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#666" }}>
                        {cityDescription || "No description available."}
                      </p>

                      {/* Map placeholder */}
                      <div
                        style={{
                          height: 160,
                          borderRadius: 8,
                          overflow: "hidden",
                          marginBottom: 12,
                          background: "#eee",
                        }}
                      >
                        <img
                          src={"/images/ra-mapbox.jpg"}
                          alt="map"
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>

                      {/* POI List */}
                      {pois.map((poi, index) => (
                        <div
                          key={poi.id}
                          style={{
                            padding: "12px",
                            border: "1px solid #eee",
                            borderRadius: "10px",
                            marginBottom: "12px",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          {/* Left side: Icon + Business info */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <img
                              src={"/icons/storeIcon.svg"}
                              alt={poi.business}
                              style={{
                                width: 25,
                                height: 25,
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 500 }}>{poi.business}</div>
                              <div style={{ fontSize: 12, color: "#888" }}>
                                {poi.businessTypeName}
                              </div>
                            </div>
                          </div>

                          {/* Right side: View More */}
                          <a style={{ fontSize: 14, color: "#1890ff", whiteSpace: "nowrap" }}>
                            View More
                          </a>
                        </div>

                      ))}
                    </div>
                  </div>

                  {/* Footer button */}
                  <Button
                    block
                    type="primary"
                    style={{
                      borderRadius: 0,
                    }}
                    onClick={() => setPreviewVisible(false)}
                  >
                    Close Preview
                  </Button>
                </div>
              </Drawer>
            </div>
          </Card>
        </Col>
      </Row >
    </div >
  );
};

export default POIRearrangePage;
